import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { IAutomationInfo, IManageAutomationModalResult, IModalContext } from '../../../../models';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { getDisplayName } from '../../../../models/NameUtils';
import {
	convertHtmlStringValueToPlainText,
	getDefaultDateStringValue,
	getScheduleTimeOptions,
} from '../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useCancelAutomationMutation,
	useCancelAutomationStepMutation,
	useUpdateAutomationStepDueDateMutation,
} from '../../../../queries/Automation';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../components-v2/Collapsible';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { toggleOn } from '../../../styles/colors';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { CloseButton } from '../../CloseButton';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { DayPicker } from '../../DayPicker';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../LocalNotificationObserver/WithNotificationService';
import { asModalComponent } from '../../Modal';
import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';
import { DefaultSelectBox, ISelectBoxOption } from '../../SelectBox';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { AutomationStepIcon } from '../../svgs/icons/AutomationStepIcon';
import { CheckmarkIconBold } from '../../svgs/icons/CheckmarkIconBold';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { AutomationStepStatusEmailEditorModal } from '../AutomationStepStatusEmailEditor';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IModalContext<IManageAutomationModalResult>,
		INotificationServiceComponentProps<IAutomationInfo> {
	automation: Api.IAutomation;
	className?: string;
	contact?: Api.IContact;
	onCancel?(): void;
	styles?: StyleDeclarationValue[];
}

const STEP_TYPES_WITH_COMMUNICATION_STATUS: Api.AutomationStepModelType[] = [
	'EmailAutomationStep',
	'HtmlNewsletterAutomationStep',
];

const CancelConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Cancel Automation',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Close',
	},
];

const CancelStepConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Cancel Step',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Close',
	},
];

function _ManageAutomation(props: IProps) {
	const {
		className,
		styles,
		automation,
		contact,
		logEvent,
		logApiError,
		logInput,
		onCancel,
		postNotification,
		parentModal,
	} = props;
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const [dueMomentToSet, setDueMomentToSet] = React.useState<moment.Moment | undefined>();
	const [popoverStepStatus, setPopoverStepStatusState] = React.useState<Api.IAutomationStepStatus | undefined>();
	const [selectedStepStatus, setSelectedStepStatus] = React.useState<Api.IAutomationStepStatus | undefined>();
	const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
	const [showCancelStepConfirmation, setShowCancelStepConfirmation] = React.useState(false);
	const [showEmailEditor, setShowEmailEditor] = React.useState(false);

	const cancelStepMutation = useCancelAutomationStepMutation({
		onSuccess: () => {
			// Post notification so parent components can refresh if needed
			postNotification?.({
				info: {
					automation,
					contact,
				},
				topic: Topics.DELETE_AUTOMATION,
			});
		},
		onError: error => {
			logApiError('DeleteStep-Error', error);
			errorMessages.pushApiError(error);
			onCancel?.();
		},
	});

	const updateDueDateMutation = useUpdateAutomationStepDueDateMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('SetStepDueDate-Error', error);
		},
	});

	const cancelAutomationMutation = useCancelAutomationMutation({
		onSuccess: () => {
			postNotification?.({
				info: {
					automation,
					contact,
				},
				topic: Topics.DELETE_AUTOMATION,
			});
			parentModal?.onRequestClose({ cancelled: true }, false);
			onCancel?.();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('Cancel-Error', error);
		},
	});

	const setPopoverStepStatus = React.useCallback(
		(stepStatus?: Api.IAutomationStepStatus) => () => {
			const dueMoment = stepStatus?.dueDate ? moment(stepStatus.dueDate) : moment().add(1, 'day').set('hours', 9);
			setDueMomentToSet(dueMoment);
			setPopoverStepStatusState(stepStatus);
		},
		[]
	);

	const handleUpdateDueDate = React.useCallback(() => {
		const dueDateStringValue = dueMomentToSet?.toISOString();
		if (dueDateStringValue && popoverStepStatus?.dueDate !== dueDateStringValue && popoverStepStatus?.id) {
			logEvent('SetStepDueDate', {
				date: dueDateStringValue,
				id: automation?.id,
				step: {
					_type: popoverStepStatus?.step?._type,
					id: popoverStepStatus?.step?.id,
				},
				stepStatusId: popoverStepStatus?.id,
			});
			updateDueDateMutation.mutate({
				stepStatusId: popoverStepStatus.id,
				dueDate: dueDateStringValue,
			});
		}
		setPopoverStepStatus(undefined)();
	}, [dueMomentToSet, popoverStepStatus, automation?.id, logEvent, updateDueDateMutation, setPopoverStepStatus]);

	const onDateSelected = React.useCallback(
		(date: Date) => {
			const due = moment(date);
			if (dueMomentToSet) {
				due.set('minutes', dueMomentToSet.get('minutes'));
				due.set('hours', dueMomentToSet.get('hours'));
			}
			setDueMomentToSet(due);
		},
		[dueMomentToSet]
	);

	const onTimeOptionSelected = React.useCallback(
		(option: ISelectBoxOption<number>) => {
			if (dueMomentToSet) {
				const due = moment(dueMomentToSet);
				due.set('hours', option.value);
				setDueMomentToSet(due);
			}
		},
		[dueMomentToSet]
	);

	const onAutomationEmailEditorModalRequestClose = React.useCallback(() => {
		setShowEmailEditor(false);
	}, []);

	const onActionMenuItemClicked = React.useCallback(
		(stepStatus: Api.IAutomationStepStatus) => (item: string) => {
			switch (item) {
				case 'edit': {
					setSelectedStepStatus(stepStatus);
					setShowEmailEditor(true);
					break;
				}
				case 'cancel': {
					logInput('CancelStep', 'Click', {
						automationId: automation?.id,
						stepId: stepStatus?.step?.id,
						stepStatusId: stepStatus.id,
						stepType: stepStatus?.step?._type,
					});
					setSelectedStepStatus(stepStatus);
					setShowCancelStepConfirmation(true);
					break;
				}
				default: {
					break;
				}
			}
		},
		[automation?.id, logInput]
	);

	const onCancelStepConfirmationModalRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			const stepToCancel = selectedStepStatus;
			setSelectedStepStatus(undefined);
			setShowCancelStepConfirmation(false);
			if (!canceled && result?.isDestructive && stepToCancel?.id) {
				cancelStepMutation.mutate({ stepStatusId: stepToCancel.id });
			}
		},
		[selectedStepStatus, cancelStepMutation]
	);

	const onCancelConfirmationModalRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			setShowCancelConfirmation(false);
			if (!canceled && result?.isDestructive && automation?.id) {
				logEvent('Cancel', { id: automation?.id });
				cancelAutomationMutation.mutate({ automationId: automation?.id });
			}
		},
		[automation?.id, logEvent, cancelAutomationMutation]
	);

	const onCancelClicked = React.useCallback(() => {
		logInput('Cancel', 'Click', { id: automation?.id });
		setShowCancelConfirmation(true);
	}, [automation?.id, logInput]);

	const renderCommunicationStatusForStep = (stepStatus: Api.IAutomationStepStatus) => {
		if (!STEP_TYPES_WITH_COMMUNICATION_STATUS.includes(stepStatus.step?._type as Api.AutomationStepModelType)) {
			return null;
		}

		const statuses = [
			{ label: 'Sent', value: stepStatus.status?.communicationStatus?.emailSent },
			{ label: 'Viewed', value: stepStatus.status?.communicationStatus?.emailOpened },
			{ label: 'Clicked', value: stepStatus.status?.communicationStatus?.emailClicked },
			{ label: 'Replied', value: stepStatus.status?.communicationStatus?.emailReplied },
		] as const;

		return (
			<div className={css(styleSheet.communicationStatus)}>
				{statuses.map(({ label, value }) => (
					<span
						key={label}
						className={css(
							styleSheet.communicationStatusBadge,
							value ? styleSheet.communicationStatusBadgeYes : styleSheet.communicationStatusBadgeNo
						)}
					>
						{value ? '✓' : '—'} {label}
					</span>
				))}
			</div>
		);
	};

	const renderNameForStepStatus = (stepStatus: Api.IAutomationStepStatus) => {
		if (stepStatus?.step?.name) {
			return stepStatus.step.name;
		}

		switch (stepStatus?.step._type) {
			case 'EmailAutomationStep': {
				return 'Email Step';
			}
			case 'HtmlNewsletterAutomationStep': {
				return 'HTML Newsletter Step';
			}
			case 'ActionItemAutomationStep': {
				const actionItemStep = stepStatus.step as Api.IActionItemAutomationStep;
				return convertHtmlStringValueToPlainText(actionItemStep.content?.document) || 'Action Item Step';
			}
			case 'AddTagAutomationStep': {
				const tagStep = stepStatus.step as Api.IAddTagAutomationStep;
				return tagStep.name || `Add Tag: "${tagStep.tagName}"`;
			}
			case 'RemoveTagAutomationStep': {
				const tagStep = stepStatus.step as Api.IRemoveTagAutomationStep;
				return tagStep.name || `Remove Tag: "${tagStep.tagName}"`;
			}
			case 'SwitchAutomationStep': {
				const s = stepStatus.step as Api.ISwitchAutomationStep;
				return s.name || `Segment`;
			}
			case 'OpportunityAutomationStep': {
				return 'Create Opportunity Step';
			}
			case 'NoActionAutomationStep': {
				return 'Do Nothing';
			}
			default: {
				return '';
			}
		}
	};

	const renderSenderForStepStatus = (automationCreator: Api.IUserReference, stepStatus: Api.IAutomationStepStatus) => {
		if (stepStatus.step.sendFromOptions && stepStatus.step.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser) {
			if (stepStatus.sender?.id) {
				return <span title={stepStatus.sender?.primaryEmail?.value}>{getDisplayName(stepStatus.sender)}</span>;
			}

			if (stepStatus.step.sendFromOptions.mode === Api.SendEmailFrom.ConnectionType) {
				return <span>{stepStatus.step.sendFromOptions.connectionType} of contact</span>;
			} else if (stepStatus.step.sendFromOptions.mode === Api.SendEmailFrom.ContactOwner) {
				return <span>Contact owner</span>;
			}
			return <span>TBD</span>;
		}

		return <span title={automationCreator?.primaryEmail?.value}>{getDisplayName(automationCreator)}</span>;
	};

	const renderStatusForStepStatus = (stepStatus: Api.IAutomationStepStatus) => {
		let statusText = stepStatus.status?.status || '';
		const textStyles: StyleDeclarationValue[] = [];
		if (stepStatus.status?.status !== Api.ProcessStatus.Completed) {
			textStyles.push(baseStyleSheet.fontBold);
		}

		switch (stepStatus?.step._type) {
			case 'EmailAutomationStep':
			case 'HtmlNewsletterAutomationStep': {
				statusText =
					stepStatus.status?.status === Api.ProcessStatus.Completed ? 'Sent' : stepStatus.status?.status || '';
				break;
			}
			case 'ActionItemAutomationStep':
			case 'AddTagAutomationStep':
			case 'RemoveTagAutomationStep': {
				statusText =
					stepStatus.status?.status === Api.ProcessStatus.Started ? 'Incomplete' : stepStatus.status?.status || '';
				break;
			}
			default: {
				break;
			}
		}

		const errorMessage = stepStatus.status?.errorMessage;

		let statusToRender: React.ReactNode = statusText;

		if (stepStatus.status?.status === Api.ProcessStatus.Completed) {
			statusToRender = (
				<div className={css(styleSheet.sentStatus)}>
					<CheckmarkIconBold fillColor={toggleOn} className={css(styleSheet.sentStatusCheckmark)} />
					<div>{statusText}</div>
				</div>
			);
		}

		if (errorMessage) {
			statusToRender = (
				<Tooltip>
					<TooltipTrigger>
						<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
							<span className={css(bs.fontBold)}>{statusText}</span>

							<InfoIcon />
						</div>
					</TooltipTrigger>
					<TooltipContent>{errorMessage}</TooltipContent>
				</Tooltip>
			);
		}

		return <div className={css(...textStyles)}>{statusToRender}</div>;
	};

	const renderDateForStepStatus = (stepStatus: Api.IAutomationStepStatus) => {
		const dateMoment = stepStatus.dueDate ? moment(stepStatus.dueDate) : null;
		let dateStringValue = dateMoment ? getDefaultDateStringValue(dateMoment) : '';
		let hoverText = 'Due date';
		const isSupportedStep =
			stepStatus.step?._type === 'EmailAutomationStep' || stepStatus.step?._type === 'HtmlNewsletterAutomationStep';
		if (
			stepStatus.status?.status === Api.ProcessStatus.Completed ||
			stepStatus.status?.status === Api.ProcessStatus.Failed ||
			stepStatus.status?.status === Api.ProcessStatus.Cancelled ||
			(!!dateMoment && dateMoment.isBefore(moment()) && stepStatus.step?._type !== 'ActionItemAutomationStep') ||
			(isSupportedStep && stepStatus.status?.status !== Api.ProcessStatus.Queued)
		) {
			switch (stepStatus.status.status) {
				case Api.ProcessStatus.Completed: {
					const originalDueDateString = dateStringValue;

					dateStringValue = getDefaultDateStringValue(moment(stepStatus.status.completedDate));
					hoverText = `Completed on: ${dateStringValue}\n\r(Original due date: ${originalDueDateString})`;
					break;
				}
				case Api.ProcessStatus.Failed: {
					if (stepStatus.status.errorDate) {
						const originalDueDateString = dateStringValue;

						dateStringValue = getDefaultDateStringValue(moment(stepStatus.status.errorDate));
						hoverText = `Failed on: ${dateStringValue}\n\r(Original due date: ${originalDueDateString})`;

						if (stepStatus.status.errorMessage) {
							hoverText += `\n\r${stepStatus.status.errorMessage}`;
						}
					}
					break;
				}
				case Api.ProcessStatus.Cancelled: {
					const originalDueDateString = dateStringValue;

					dateStringValue = getDefaultDateStringValue(moment(stepStatus.status.cancelledDate));
					hoverText = `Canceled on: ${dateStringValue}\n\r(Original due date: ${originalDueDateString})`;
					break;
				}
				default: {
					break;
				}
			}
			return <div title={hoverText}>{dateStringValue}</div>;
		}
		if (!!dateMoment || (!dateMoment && stepStatus.step?._type === 'ActionItemAutomationStep')) {
			const anchor = (
				<button
					className={css(baseStyleSheet.brandLink, styleSheet.stepsTableBodyRowEditableDate)}
					onClick={setPopoverStepStatus(stepStatus)}
				>
					<span>{dateMoment ? dateStringValue : 'Set due date'}</span>
				</button>
			);

			let timeDropDown: React.ReactNode;
			if (
				!!popoverStepStatus &&
				!!dueMomentToSet &&
				(stepStatus?.step?._type === 'EmailAutomationStep' ||
					stepStatus?.step?._type === 'HtmlNewsletterAutomationStep')
			) {
				const minutes = dueMomentToSet.get('minutes') || 0;
				const hours = dueMomentToSet.get('hours');
				const scheduleOptions = getScheduleTimeOptions(minutes, userSession, undefined, dueMomentToSet);
				timeDropDown = (
					<div className={css(styleSheet.dayPickerPopoverTime)}>
						<DefaultSelectBox
							className={css(styleSheet.dayPickerPopoverTimeDropDown)}
							menuPopupPosition='top'
							onSelectionChanged={onTimeOptionSelected}
							options={scheduleOptions}
							optionStyles={[styleSheet.dayPickerPopoverTimeDropdownOption]}
							selectedOption={hours !== null ? scheduleOptions.find(x => x.value === hours) : undefined}
							triggerStyles={[styleSheet.dayPickerPopoverTimeDropdownTrigger]}
						/>
					</div>
				);
			}

			const selectedDate = dueMomentToSet?.toDate();
			return (
				<Popover open={!!popoverStepStatus?.step?.id && popoverStepStatus.step.id === stepStatus?.step?.id}>
					<PopoverTrigger asChild>{anchor}</PopoverTrigger>
					<PopoverContent onInteractOutside={setPopoverStepStatus(undefined)}>
						<div className={css(styleSheet.dayPickerPopoverContent)}>
							<div className={css(styleSheet.dayPickerPopoverHeader)}>
								<CloseButton onClick={setPopoverStepStatus(undefined)} />
							</div>
							<DayPicker defaultSelectedDay={selectedDate} onDayClick={onDateSelected} selectedDays={selectedDate} />
							{timeDropDown}
							<div className={css(styleSheet.dayPickerPopoverFooter)}>
								<button className={css(baseStyleSheet.ctaButtonSmall)} onClick={handleUpdateDueDate}>
									<span>{`Update date${popoverStepStatus?.step?._type === 'EmailAutomationStep' ? ' & time' : ''}`}</span>
								</button>
							</div>
						</div>
					</PopoverContent>
				</Popover>
			);
		}
		return '';
	};

	if (!automation) {
		return null;
	}

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} manage-automation ${className || ''}`}>
			<div className={css(styleSheet.header)}>Manage Automation</div>
			<div className={css(styleSheet.actionBar)}>
				<div className={css(styleSheet.actionBarName)}>
					<div className={css(baseStyleSheet.tableColumnHeader, styleSheet.actionBarLabel)}>Automation Name</div>
					<NavLink
						className={css(baseStyleSheet.brandLink)}
						title='Edit automation template'
						to={`/automations/${automation?.templateId}`}
					>
						{automation?.name}
					</NavLink>
				</div>
				{(automation?.status?.status === Api.ProcessStatus.Queued ||
					automation?.status?.status === Api.ProcessStatus.Started) && (
					<button className={css(baseStyleSheet.ctaButtonSmall)} onClick={onCancelClicked}>
						<span>Cancel Automation</span>
					</button>
				)}
			</div>
			<div className={css(styleSheet.stepsTable)}>
				<div className={css(baseStyleSheet.tableColumnHeader, styleSheet.stepsTableRow)}>
					<div className={css(styleSheet.stepsTableNameCol)}>Steps</div>
					<div className={css(styleSheet.stepsTableSenderCol)}>Sender</div>
					<div className={css(styleSheet.stepsTableStatusCol)}>Status</div>
					<div className={css(styleSheet.stepsTableDateCol)}>Date</div>
					<div className={css(styleSheet.stepsTableActionsCol)}>Actions</div>
				</div>
				{automation?.steps?.map((x, i) => {
					const showMenuButton = !x?.isFinished && !x?.step?.isDependant;
					return (
						<Collapsible key={x.step?.id || i}>
							<div className={css(bs.flex, bs.flexCol)}>
								<div className={css(styleSheet.stepsTableRow, styleSheet.stepsTableBodyRow)}>
									<div
										className={css(
											baseStyleSheet.truncateText,
											baseStyleSheet.horizontalStack,
											styleSheet.stepsTableNameCol
										)}
									>
										<AutomationStepIcon
											styles={[styleSheet.stepsTableBodyRowNameIcon]}
											type={x.step?._type as Api.AutomationStepModelType}
										/>
										{STEP_TYPES_WITH_COMMUNICATION_STATUS.includes(x.step?._type as Api.AutomationStepModelType) ? (
											<CollapsibleTrigger className={css(styleSheet.stepsTableBodyRowNameTrigger)}>
												<div className={css(baseStyleSheet.truncateText, styleSheet.stepsTableBodyRowName)}>
													{renderNameForStepStatus(x)}
													<ChevronDown
														height={18}
														width={18}
														className={css(styleSheet.stepsTableBodyRowNameTriggerIcon)}
													/>
												</div>
											</CollapsibleTrigger>
										) : (
											<div className={css(styleSheet.stepsTableBodyRowNameTrigger)}>
												<div className={css(baseStyleSheet.truncateText, styleSheet.stepsTableBodyRowName)}>
													{renderNameForStepStatus(x)}
												</div>
											</div>
										)}
									</div>
									<div className={css(styleSheet.stepsTableSenderCol)}>
										{renderSenderForStepStatus(automation.creator, x)}
									</div>
									<div className={css(styleSheet.stepsTableStatusCol)}>{renderStatusForStepStatus(x)}</div>
									<div className={css(styleSheet.stepsTableDateCol)}>{renderDateForStepStatus(x)}</div>
									<div className={css(styleSheet.stepsTableActionsCol)}>
										{showMenuButton ? (
											<Dropdown>
												<DropdownEllipsisTrigger />
												<DropdownContent>
													{x?.status?.status === Api.ProcessStatus.Queued &&
													(x?.step?._type === 'EmailAutomationStep' ||
														x?.step?._type === 'HtmlNewsletterAutomationStep') ? (
														<DropdownItem onClick={() => onActionMenuItemClicked(x)('edit')}>Edit</DropdownItem>
													) : null}
													<DropdownItem onClick={() => onActionMenuItemClicked(x)('cancel')}>Cancel Step</DropdownItem>
												</DropdownContent>
											</Dropdown>
										) : null}
									</div>
								</div>
								{STEP_TYPES_WITH_COMMUNICATION_STATUS.includes(x.step?._type as Api.AutomationStepModelType) && (
									<CollapsiblePanel>{renderCommunicationStatusForStep(x)}</CollapsiblePanel>
								)}
							</div>
						</Collapsible>
					);
				})}
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showCancelConfirmation,
					onRequestClose: onCancelConfirmationModalRequestClose,
				}}
				options={CancelConfirmationOptions}
				title='Are you sure you want to cancel this automation?'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showCancelStepConfirmation,
					onRequestClose: onCancelStepConfirmationModalRequestClose,
				}}
				options={CancelStepConfirmationOptions}
				title='Are you sure you want to cancel this step?'
			/>
			<AutomationStepStatusEmailEditorModal
				contact={contact}
				emailStepStatus={
					showEmailEditor ? (selectedStepStatus as Api.IAutomationStepStatus<Api.IEmailAutomationStep>) : undefined
				}
				modalProps={{
					isOpen: showEmailEditor,
					onRequestClose: onAutomationEmailEditorModalRequestClose,
				}}
			/>
		</div>
	);
}

const ManageAutomationWithLocalNotifications = withNotificationService(_ManageAutomation);
export const ManageAutomation = withEventLogging(ManageAutomationWithLocalNotifications, 'ManageAutomation');

export const ManageAutomationModal = asModalComponent(ManageAutomation, {
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: false,
	useStyledHeader: true,
	contentPresentationStyle: {
		className: `modal-overlay-content ${css(styleSheet.modalContent)}`,
		transitionInDuration: 450,
		transitionOutDuration: 450,
	},
});
